import _ from 'lodash';
import pluralize from 'pluralize';
import React, {FunctionComponent, useContext, useState} from 'react';
import {useHistory} from 'react-router-dom';
import {useSelector} from 'react-redux';
import {ProductInCatalog} from '@treez/commons/sharedTypings/product';
import {AddIcon} from '@treez-ui/core/icons';
import {Button, ButtonClass, CreateButton} from '@treez-ui/core/buttons';
import {
    CurrentSelectionStateContext,
    DispatchCurrentSelectionAction,
    TypeOfSelectAction,
} from '~/lib/selectedProductContext';
import ActionBar from '~/modules/widgets/actionBar/ActionBar';
import StoreSelectorForMultipleProducts, {
    StoreSelectorAction,
} from '~/modules/widgets/modals/StoreSelectorForMultipleProducts';
import {
    selectOrganizationCatalogId,
    selectOrganizationId,
} from '~/redux/selectors/organizationSelectors';
import {generateProductMergeRoute} from '~/Routes';
import {generateQueryString} from '~/lib/routing';

interface Props {
    openBrowseBrandTreezPrompt: () => void;
}

export const OrganizationCatalogActions: FunctionComponent<Props> = ({
    openBrowseBrandTreezPrompt,
}: Props) => {
    const selectedProducts = useContext(CurrentSelectionStateContext);
    const dispatchSelectedProducts = DispatchCurrentSelectionAction();

    const organizationId = useSelector(selectOrganizationId);
    const organizationCatalogId = useSelector(selectOrganizationCatalogId);

    const history = useHistory();

    const [storeSelectorOpen, setStoreSelectorOpen] = useState<boolean>(false);

    const clearSelection = () => {
        dispatchSelectedProducts({
            type: TypeOfSelectAction.CLEAR,
        });
    };

    const redirectToMerge = (productsToMerge: ProductInCatalog[]) => {
        const productsToMergeIds = _.map(productsToMerge, 'id');
        const {pathname, search} = generateProductMergeRoute(organizationId, productsToMergeIds, {
            hideExactMatch: false,
        });

        history.push({
            pathname,
            search,
            state: {
                productsToMerge,
                organizationCatalogId,
                refferer: generateQueryString(location.search),
            },
        });
    };

    const selectedProductTypes = _.map(selectedProducts, 'type');
    const singleTypeSelected =
        _.size(_.uniq(selectedProductTypes)) === 1 && selectedProducts.length > 1;

    const addProductsToStoresButton = (
        <Button
            key="add-product-to-store-button"
            buttonClass={ButtonClass.QUIET}
            icon={<AddIcon />}
            text={`Add ${pluralize('product', selectedProducts.length)}`}
            onClick={() => setStoreSelectorOpen(true)}
        />
    );

    const mergeProductsButton = (
        <Button
            key="merge-products-button"
            buttonClass={ButtonClass.PREFERRED}
            text="Merge"
            onClick={() => redirectToMerge(selectedProducts)}
        />
    );

    const actions = singleTypeSelected
        ? [addProductsToStoresButton, mergeProductsButton]
        : [addProductsToStoresButton];

    return (
        <div className={`actionBarContainer ${selectedProducts.length > 0 ? 'selected' : ''}`}>
            <CreateButton onClick={openBrowseBrandTreezPrompt} />
            <ActionBar
                actions={actions}
                clearSelection={clearSelection}
                numberSelected={selectedProducts.length}
            />
            <StoreSelectorForMultipleProducts // Add Multiple Products to Stores Modal
                addOrRemove={StoreSelectorAction.ADD}
                close={() => setStoreSelectorOpen(false)}
                open={storeSelectorOpen}
            />
        </div>
    );
};

export default OrganizationCatalogActions;
